'use client'

import { usePathname } from 'next/navigation'
import * as React from 'react'

type TopNavTitleContextValue = {
  title: string | null
  setTitle: (title: string | null) => void
  titleLeftHref: string | null
  setTitleLeftHref: (href: string | null) => void
}

const TopNavTitleContext = React.createContext<TopNavTitleContextValue | undefined>(undefined)

function leftLabelFromTitle(title: string) {
  const t = String(title || '')
  const idx = t.indexOf(' / ')
  return idx === -1 ? t : t.slice(0, idx)
}

function allowedPrefixesForLeftLabel(label: string, pathname: string): string[] | null {
  const l = String(label || '').trim()
  if (!l) return null
  // Agent IDE session titles are free-form chat titles.
  if (pathname.startsWith('/agent-ide/session/')) return ['/agent-ide/session/']
  if (pathname.startsWith('/v2/agent-ide/session/')) return ['/v2/agent-ide/session/']
  if (pathname.startsWith('/agents/')) return ['/agents/']
  // When on a project sub-route (e.g. candidates-feedback), allow any left label (e.g. project name).
  if (pathname.startsWith('/projects/')) return ['/projects']
  if (l === 'Agent') return ['/agent']
  if (l === 'Workflows') return ['/workflows', '/workflow']
  if (l === 'Projects') return ['/projects', '/project']
  if (l === 'Fold') return ['/fold']
  if (l === 'MD Simulation') return ['/openmm']
  if (l === 'Design') return ['/design']
  if (l === 'Properties') return ['/properties']
  if (l === 'Library') return ['/library']
  if (l === 'Jobs') return ['/jobs', '/sequences', '/tasks/results', '/openmm']
  if (l === 'Usage') return ['/usage']
  if (l === 'API Keys') return ['/api-keys']
  if (l === 'Models') return ['/models']
  if (l === 'Integrations') return ['/integrations']
  if (l === 'Mol* Nano Protein') return ['/mol']
  if (l === 'Py2DMol') return ['/py2dmol']
  if (l === 'Code Viewer') return ['/code']
  if (l === 'Settings') return ['/settings', '/teams/']
  if (l === 'Login') return ['/login']
  if (l === 'Sign up') return ['/signup']
  return null
}

export function TopNavTitleProvider({ children }: { children: React.ReactNode }) {
  const [title, setTitle] = React.useState<string | null>(null)
  const [titleLeftHref, setTitleLeftHref] = React.useState<string | null>(null)

  const pathname = usePathname()
  React.useEffect(() => {
    if (!title) return
    const p = String(pathname || '')
    const left = leftLabelFromTitle(title)
    const allowed = allowedPrefixesForLeftLabel(left, p)
    // If we can’t confidently match this title to the current route section,
    // clear it so `SharedTopNav` falls back to pathname-derived titles.
    if (!allowed) {
      setTitle(null)
      setTitleLeftHref(null)
      return
    }
    const ok = allowed.some((prefix) => (prefix === '/agent' ? p.startsWith('/agent') : p.startsWith(prefix)))
    if (!ok) {
      setTitle(null)
      setTitleLeftHref(null)
    }
  }, [pathname, title])

  const value = React.useMemo(
    () => ({ title, setTitle, titleLeftHref, setTitleLeftHref }),
    [title, titleLeftHref]
  )
  return <TopNavTitleContext.Provider value={value}>{children}</TopNavTitleContext.Provider>
}

export function useTopNavTitle() {
  const ctx = React.useContext(TopNavTitleContext)
  if (!ctx) {
    throw new Error('useTopNavTitle must be used within TopNavTitleProvider')
  }
  return ctx
}

